import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Cookie, Settings, Shield, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Immer aktiviert
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setPreferences(necessaryOnly);
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (category, value) => {
    if (category === 'necessary') return; // Necessary cookies können nicht deaktiviert werden
    setPreferences(prev => ({ ...prev, [category]: value }));
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4"
          >
            <Card className="bg-white shadow-2xl border border-gray-200">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <Cookie className="w-6 h-6 text-green-700 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Cookies & Datenschutz</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Wir verwenden Cookies, um Ihnen die bestmögliche Nutzererfahrung zu bieten. 
                        Notwendige Cookies sind für die Funktionalität der Website erforderlich. 
                        Weitere Cookies helfen uns dabei, unsere Website zu verbessern.
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <Link 
                          to={createPageUrl('Datenschutz')} 
                          className="text-green-700 hover:underline"
                        >
                          Datenschutzerklärung
                        </Link>
                        <span className="text-gray-400">•</span>
                        <Link 
                          to={createPageUrl('Impressum')} 
                          className="text-green-700 hover:underline"
                        >
                          Impressum
                        </Link>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSettings(true)}
                      className="w-full sm:w-auto"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Einstellungen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAcceptNecessary}
                      className="w-full sm:w-auto"
                    >
                      Nur Notwendige
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAcceptAll}
                      className="bg-green-700 hover:bg-green-800 w-full sm:w-auto"
                    >
                      Alle akzeptieren
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Shield className="w-5 h-5 mr-2 text-green-700" />
              Cookie-Einstellungen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox 
                  checked={preferences.necessary} 
                  disabled={true}
                  className="mt-1"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Notwendige Cookies</h4>
                  <p className="text-sm text-gray-600">
                    Diese Cookies sind für die grundlegenden Funktionen der Website erforderlich 
                    und können nicht deaktiviert werden. Sie speichern keine persönlich 
                    identifizierbaren Informationen.
                  </p>
                  <div className="mt-2 text-xs text-gray-500">
                    Beispiele: Sitzungs-ID, Spracheinstellungen, Sicherheits-Token
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox 
                  checked={preferences.analytics} 
                  onCheckedChange={(checked) => handlePreferenceChange('analytics', checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Analytische Cookies</h4>
                  <p className="text-sm text-gray-600">
                    Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen, 
                    indem sie anonyme Informationen sammeln und melden.
                  </p>
                  <div className="mt-2 text-xs text-gray-500">
                    Beispiele: Seitenaufrufe, Verweildauer, Herkunft des Traffics
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox 
                  checked={preferences.marketing} 
                  onCheckedChange={(checked) => handlePreferenceChange('marketing', checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Marketing Cookies</h4>
                  <p className="text-sm text-gray-600">
                    Diese Cookies werden verwendet, um Werbung relevanter für Sie 
                    und Ihre Interessen zu machen.
                  </p>
                  <div className="mt-2 text-xs text-gray-500">
                    Beispiele: Anzeigen-Personalisierung, Conversion-Tracking
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="font-semibold text-blue-900 mb-2">Ihre Rechte</h5>
              <p className="text-sm text-blue-800">
                Sie können Ihre Cookie-Einstellungen jederzeit ändern oder widerrufen. 
                Weitere Informationen finden Sie in unserer{' '}
                <Link to={createPageUrl('Datenschutz')} className="underline hover:no-underline">
                  Datenschutzerklärung
                </Link>.
              </p>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => setShowSettings(false)}
            >
              Abbrechen
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleAcceptNecessary}
              >
                Nur Notwendige
              </Button>
              <Button 
                onClick={handleSaveSettings}
                className="bg-green-700 hover:bg-green-800"
              >
                Einstellungen speichern
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
